import { createORPCClient, MalformedResponseError, ORPCError } from "@orpc/client";
import type { RouterContractClient } from "@orpc/contract";
import type { JsonifiedClient } from "@orpc/openapi";
import { OpenAPILink } from "@orpc/openapi/fetch";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { contract } from "@pax/api-contract";
import { type ClientConfig, clientConfig } from "@pax/api-contract/client-config";
import { contract as gameServerContract } from "@pax/game-server-contract";
import { paxFetch } from "./network";

export const UNREACHABLE_MESSAGE = "Pax is unreachable right now. Try again in a moment.";

/** A declared refusal the API answered with, as the risk store needs it. */
export interface ApiRefusal {
  code: string;
  data: unknown;
  requestId: string | null;
  /** From `retry-after`, when the API said how long to wait. */
  retryAfterSeconds: number | null;
}

type AuthorizationLossHandler = () => Promise<void>;
/**
 * Explains or resolves a refusal, resolving true when the refused request is worth retrying.
 * `attempt` counts the retries the request already made. A `background` request is one the person
 * didn't ask for, so only a step they can take is put in front of them.
 */
type RefusalHandler = (
  refusal: ApiRefusal,
  attempt: number,
  request: { background: boolean },
) => Promise<boolean>;

const REFUSAL_STATUSES = new Set([402, 403, 429, 503]);
const MAX_REFUSAL_RETRIES = 3;

let authorizationLossHandler: AuthorizationLossHandler | undefined;
let sessionRefresh: Promise<void> | undefined;
let refusalHandler: RefusalHandler | undefined;

export function registerAuthorizationLossHandler(handler: AuthorizationLossHandler): () => void {
  authorizationLossHandler = handler;
  return () => {
    if (authorizationLossHandler === handler) authorizationLossHandler = undefined;
  };
}

export function registerRefusalHandler(handler: RefusalHandler): () => void {
  refusalHandler = handler;
  return () => {
    if (refusalHandler === handler) refusalHandler = undefined;
  };
}

async function readRefusal(response: Response): Promise<ApiRefusal | undefined> {
  if (!REFUSAL_STATUSES.has(response.status)) return undefined;
  const body: unknown = await response
    .clone()
    .json()
    .catch(() => null);
  if (typeof body !== "object" || body === null || !("code" in body)) return undefined;
  if (typeof body.code !== "string") return undefined;
  const retryAfter = Number(response.headers.get("retry-after"));
  return {
    code: body.code,
    data: "data" in body ? body.data : undefined,
    requestId: response.headers.get("request-id"),
    retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
  };
}

/**
 * Sends a request, handing every refusal to the risk store: a step it can complete retries the
 * request, and anything else is explained to the person while the original response comes back.
 * The AI routes answer refusals in the same shape, so direct calls to them use this too.
 */
export async function sendWithRefusalHandling(
  send: () => Promise<Response>,
  { background = false }: { background?: boolean } = {},
): Promise<Response> {
  let response = await send();
  for (let attempt = 0; attempt < MAX_REFUSAL_RETRIES; attempt++) {
    const refusal = refusalHandler && (await readRefusal(response));
    if (!refusal || !refusalHandler) return response;
    if (!(await refusalHandler(refusal, attempt, { background }).catch(() => false))) {
      return response;
    }
    response = await send();
  }
  return response;
}

function reportAuthorizationLoss(): void {
  if (!authorizationLossHandler || sessionRefresh) return;
  sessionRefresh = authorizationLossHandler()
    .catch(() => undefined)
    .finally(() => {
      sessionRefresh = undefined;
    });
}

function createApiClient({
  credentials,
  cache,
  reportAuthenticationLoss = false,
  background = false,
}: {
  credentials: RequestCredentials;
  cache?: RequestCache;
  reportAuthenticationLoss?: boolean;
  background?: boolean;
}) {
  const link = new OpenAPILink(contract, {
    fetch: async (request, init) => {
      const response = await sendWithRefusalHandling(
        () => paxFetch(request, { ...init, credentials, ...(cache ? { cache } : {}) }),
        { background },
      );
      if (reportAuthenticationLoss && response.status === 401) reportAuthorizationLoss();
      return response;
    },
  });
  return createORPCClient<JsonifiedClient<RouterContractClient<typeof contract>>>(link);
}

export const api = createApiClient({ credentials: "include", reportAuthenticationLoss: true });
export const orpc = createTanstackQueryUtils(api);

/** For requests the app makes on its own, such as checking in a session. */
export const backgroundApi = createApiClient({
  credentials: "include",
  reportAuthenticationLoss: true,
  background: true,
});

export const publicApi = createApiClient({ credentials: "same-origin" });
export const publicOrpc = createTanstackQueryUtils(publicApi);

/** The game server on this origin, which reads the sign-in cookie only to connect. */
export const gameServer = createORPCClient<
  JsonifiedClient<RouterContractClient<typeof gameServerContract>>
>(
  new OpenAPILink(gameServerContract, {
    fetch: async (request, init) => {
      const response = await paxFetch(request, { ...init, credentials: "same-origin" });
      if (response.status === 401) reportAuthorizationLoss();
      return response;
    },
  }),
);

/** Public reads the browser must revalidate instead of answering from its own cached copy. */
export const revalidatedPublicApi = createApiClient({
  credentials: "same-origin",
  cache: "no-cache",
});

export async function fetchClientConfig(): Promise<ClientConfig> {
  let response: Response;
  try {
    response = await paxFetch(clientConfig.path);
  } catch {
    throw new Error(UNREACHABLE_MESSAGE);
  }
  if (!response.ok) throw new Error(UNREACHABLE_MESSAGE);
  return clientConfig.response.parse(await response.json());
}

export const isUnauthorized = (error: unknown): boolean =>
  error instanceof ORPCError && error.code === "UNAUTHORIZED";

export const isNotFound = (error: unknown): boolean =>
  error instanceof ORPCError && error.code === "NOT_FOUND";

export const isAccountRequired = (error: unknown): boolean =>
  error instanceof ORPCError && error.code === "ACCOUNT_REQUIRED";

/**
 * What to tell a person about a failed request. An error the contract declares carries a message
 * written for them; an undeclared one is the server failing unexpectedly.
 */
export function requestErrorMessage(error: unknown): string {
  if (error instanceof ORPCError) {
    return error.defined ? error.message : "Pax ran into a problem. Try again in a moment.";
  }
  return UNREACHABLE_MESSAGE;
}

export async function reachable<Result>(request: Promise<Result>): Promise<Result> {
  try {
    return await request;
  } catch (error) {
    if (error instanceof ORPCError && error.cause instanceof MalformedResponseError) {
      throw new Error(UNREACHABLE_MESSAGE, { cause: error });
    }
    throw error;
  }
}
